/**
 * Custom Emoji Configuration
 *
 * Add your custom emojis here. See EMOJI_README.md for documentation.
 *
 * Note: Standard emoji shortcodes (like :heart:, :fire:, :smile:) are handled
 * automatically by the remark-emoji library. This file only needs to contain
 * your custom emojis (like Twitch emotes).
 */

export interface CustomEmoji {
    /** The primary name/identifier for this emoji */
    name: string;
    /** URL path to the emoji image (relative to public folder) */
    url: string;
    /** Alternative names that can be used with colons, e.g., :alias: */
    aliases?: string[];
    /**
     * If true, matches the raw emoji name without colons.
     * Default: true for names starting with lowercase or containing numbers
     * (like Twitch emotes: vander60PRAY, PogChamp, Kappa)
     */
    matchWithoutColons?: boolean;
}

/**
 * Custom emojis for this project.
 * Add your Twitch emotes, server emojis, etc. here.
 *
 * Note: Place emoji image files in /public/emojis/
 */
export const CUSTOM_EMOJIS: Record<string, CustomEmoji> = {
    // Numbered user emojis
    "01CAGWA": { name: "01CAGWA", url: "/emojis/01CAGWA.png" },
    "102FLICKSART": { name: "102FLICKSART", url: "/emojis/102FLICKSART.png" },
    "104COOKIESAURUS": {
        name: "104COOKIESAURUS",
        url: "/emojis/104COOKIESAURUS.png",
    },
    "114NORTHERNLIGHTS16": {
        name: "114NORTHERNLIGHTS16",
        url: "/emojis/114NORTHERNLIGHTS16.png",
    },
    "117ZIGE_THEBUSDADDY": {
        name: "117ZIGE_THEBUSDADDY",
        url: "/emojis/117ZIGE_THEBUSDADDY.png",
    },
    "119KAMIKAZEKRISTY": {
        name: "119KAMIKAZEKRISTY",
        url: "/emojis/119KAMIKAZEKRISTY.png",
    },
    "148AHNSCHLOTT": {
        name: "148AHNSCHLOTT",
        url: "/emojis/148AHNSCHLOTT.png",
    },
    "150GINZU99": { name: "150GINZU99", url: "/emojis/150GINZU99.png" },
    "152DOTHACKERNA": {
        name: "152DOTHACKERNA",
        url: "/emojis/152DOTHACKERNA.png",
    },
    "156GOOD_GRIEF115": {
        name: "156GOOD_GRIEF115",
        url: "/emojis/156GOOD_GRIEF115.png",
    },
    "170GRIMRFANG": { name: "170GRIMRFANG", url: "/emojis/170GRIMRFANG.png" },
    "171JNFLICK": { name: "171JNFLICK", url: "/emojis/171JNFLICK.png" },
    "191MAJORPAIN_OG": {
        name: "191MAJORPAIN_OG",
        url: "/emojis/191MAJORPAIN_OG.png",
    },
    "200BROKENYELLOWCRAYON": {
        name: "200BROKENYELLOWCRAYON",
        url: "/emojis/200BROKENYELLOWCRAYON.png",
    },
    "215WOLFE_GUARD": {
        name: "215WOLFE_GUARD",
        url: "/emojis/215WOLFE_GUARD.png",
    },
    "219NORGON_WARRIOR": {
        name: "219NORGON_WARRIOR",
        url: "/emojis/219NORGON_WARRIOR.png",
    },
    "228MRS_GRIEF1111": {
        name: "228MRS_GRIEF1111",
        url: "/emojis/228MRS_GRIEF1111.png",
    },
    "231GINOVAE": { name: "231GINOVAE", url: "/emojis/231GINOVAE.png" },
    "236BADHAG": { name: "236BADHAG", url: "/emojis/236BADHAG.png" },
    "23TOZUKA": { name: "23TOZUKA", url: "/emojis/23TOZUKA.png" },
    "24STABB3DBYSHADOW": {
        name: "24STABB3DBYSHADOW",
        url: "/emojis/24STABB3DBYSHADOW.png",
    },
    "269ALTHESUPERIOR": {
        name: "269ALTHESUPERIOR",
        url: "/emojis/269ALTHESUPERIOR.png",
    },
    "277CINNAMON_SANTA": {
        name: "277CINNAMON_SANTA",
        url: "/emojis/277CINNAMON_SANTA.png",
    },
    "280GEMMA15": { name: "280GEMMA15", url: "/emojis/280GEMMA15.png" },
    "29KHAOS_TTV": { name: "29KHAOS_TTV", url: "/emojis/29KHAOS_TTV.png" },
    "306FIERYBLACKFANG": {
        name: "306FIERYBLACKFANG",
        url: "/emojis/306FIERYBLACKFANG.png",
    },
    "44TWSTEDPIXITV": {
        name: "44TWSTEDPIXITV",
        url: "/emojis/44TWSTEDPIXITV.png",
    },
    "47MALIBUANGEL": {
        name: "47MALIBUANGEL",
        url: "/emojis/47MALIBUANGEL.png",
    },
    "48MIMIDOTB": { name: "48MIMIDOTB", url: "/emojis/48MIMIDOTB.png" },
    "50TARDISELECTRA": {
        name: "50TARDISELECTRA",
        url: "/emojis/50TARDISELECTRA.png",
    },
    "64SAUCYPOPPET": {
        name: "64SAUCYPOPPET",
        url: "/emojis/64SAUCYPOPPET.png",
    },
    "68SILFRBLAD": { name: "68SILFRBLAD", url: "/emojis/68SILFRBLAD.png" },
    "78DARKSEWERRAT": {
        name: "78DARKSEWERRAT",
        url: "/emojis/78DARKSEWERRAT.png",
    },
    "81BLUEFOX5000000": {
        name: "81BLUEFOX5000000",
        url: "/emojis/81BLUEFOX5000000.png",
    },
    "85MOOSEPAI": { name: "85MOOSEPAI", url: "/emojis/85MOOSEPAI.png" },
    "98UNCLERYANTV": {
        name: "98UNCLERYANTV",
        url: "/emojis/98UNCLERYANTV.png",
    },

    // Named emojis
    BRIGHTSIDEDEFENDER: {
        name: "BRIGHTSIDEDEFENDER",
        url: "/emojis/BRIGHTSIDEDEFENDER.png",
    },
    CAGWA: { name: "CAGWA", url: "/emojis/CAGWA.png" },
    EMERALDBOUNTY: { name: "EMERALDBOUNTY", url: "/emojis/EMERALDBOUNTY.png" },
    EXALTED: { name: "EXALTED", url: "/emojis/EXALTED.png" },
    FLICKSART: { name: "FLICKSART", url: "/emojis/FLICKSART.png" },
    FREYAI: { name: "FREYAI", url: "/emojis/FREYAI.png" },
    FREYAII: { name: "FREYAII", url: "/emojis/FREYAII.png" },
    GODMARKOFHEL: { name: "GODMARKOFHEL", url: "/emojis/GODMARKOFHEL.png" },
    GODMARKOFHODR: { name: "GODMARKOFHODR", url: "/emojis/GODMARKOFHODR.png" },
    GODMARKOFLOKI: { name: "GODMARKOFLOKI", url: "/emojis/GODMARKOFLOKI.png" },
    GODMARKOFSKADI: {
        name: "GODMARKOFSKADI",
        url: "/emojis/GODMARKOFSKADI.png",
    },
    GOLDBOUNTY: { name: "GOLDBOUNTY", url: "/emojis/GOLDBOUNTY.png" },
    HORIZONSGRACE: { name: "HORIZONSGRACE", url: "/emojis/HORIZONSGRACE.png" },
    KHAOS: { name: "KHAOS", url: "/emojis/KHAOS.png" },
    KVINNA: { name: "KVINNA", url: "/emojis/KVINNA.png" },
    MAGNII: { name: "MAGNII", url: "/emojis/MAGNII.png" },
    MIMI: { name: "MIMI", url: "/emojis/MIMI.png" },
    OTHALA: { name: "OTHALA", url: "/emojis/OTHALA.png" },
    RAIDCHAMP: { name: "RAIDCHAMP", url: "/emojis/RAIDCHAMP.png" },
    REPTILE: { name: "REPTILE", url: "/emojis/REPTILE.png" },
    RUBYBOUNTY: { name: "RUBYBOUNTY", url: "/emojis/RUBYBOUNTY.png" },
    SAPPHIREBOUNTY: {
        name: "SAPPHIREBOUNTY",
        url: "/emojis/SAPPHIREBOUNTY.png",
    },
    SEASOFMIDGARD: { name: "SEASOFMIDGARD", url: "/emojis/SEASOFMIDGARD.png" },
    SILFRBLAD: { name: "SILFRBLAD", url: "/emojis/SILFRBLAD.png" },
    SKAL: { name: "SKAL", url: "/emojis/SKAL.png" },
    THE100: { name: "THE100", url: "/emojis/THE100.png" },
    THE500: { name: "THE500", url: "/emojis/THE500.png" },
    TOZUKA: { name: "TOZUKA", url: "/emojis/TOZUKA.png" },
    VP: { name: "VP", url: "/emojis/VP.png" },
    WoH: { name: "WoH", url: "/emojis/WoH.png" },

    // vander60 Twitch emotes
    vander60ALGIZEHWAZ: {
        name: "vander60ALGIZEHWAZ",
        url: "/emojis/vander60ALGIZEHWAZ.png",
    },
    vander60ASW: { name: "vander60ASW", url: "/emojis/vander60ASW.png" },
    vander60ASWSHIELD: {
        name: "vander60ASWSHIELD",
        url: "/emojis/vander60ASWSHIELD.png",
    },
    vander60CARSON: {
        name: "vander60CARSON",
        url: "/emojis/vander60CARSON.png",
    },
    vander60CRY: { name: "vander60CRY", url: "/emojis/vander60CRY.png" },
    vander60CUT: { name: "vander60CUT", url: "/emojis/vander60CUT.png" },
    vander60DANCE: { name: "vander60DANCE", url: "/emojis/vander60DANCE.png" },
    vander60DRUMS: { name: "vander60DRUMS", url: "/emojis/vander60DRUMS.png" },
    vander60EMC: { name: "vander60EMC", url: "/emojis/vander60EMC.png" },
    vander60EXALTED: {
        name: "vander60EXALTED",
        url: "/emojis/vander60EXALTED.png",
    },
    vander60GATSBY: {
        name: "vander60GATSBY",
        url: "/emojis/vander60GATSBY.png",
    },
    vander60GOLDWOHTOKEN: {
        name: "vander60GOLDWOHTOKEN",
        url: "/emojis/vander60GOLDWOHTOKEN.png",
    },
    vander60GOTA: { name: "vander60GOTA", url: "/emojis/vander60GOTA.png" },
    vander60HAMMER: {
        name: "vander60HAMMER",
        url: "/emojis/vander60HAMMER.png",
    },
    vander60HANDAXE: {
        name: "vander60HANDAXE",
        url: "/emojis/vander60HANDAXE.png",
    },
    vander60HYSTERIA: {
        name: "vander60HYSTERIA",
        url: "/emojis/vander60HYSTERIA.png",
    },
    vander60IRONSWORD: {
        name: "vander60IRONSWORD",
        url: "/emojis/vander60IRONSWORD.png",
    },
    vander60JORMYBODY: {
        name: "vander60JORMYBODY",
        url: "/emojis/vander60JORMYBODY.png",
    },
    vander60JORMYHEAD: {
        name: "vander60JORMYHEAD",
        url: "/emojis/vander60JORMYHEAD.png",
    },
    vander60JORMYTAIL: {
        name: "vander60JORMYTAIL",
        url: "/emojis/vander60JORMYTAIL.png",
    },
    vander60KROS: { name: "vander60KROS", url: "/emojis/vander60KROS.png" },
    vander60LOL: { name: "vander60LOL", url: "/emojis/vander60LOL.png" },
    vander60MFG: { name: "vander60MFG", url: "/emojis/vander60MFG.png" },
    vander60MFGSHIELD: {
        name: "vander60MFGSHIELD",
        url: "/emojis/vander60MFGSHIELD.png",
    },
    vander60NSD: { name: "vander60NSD", url: "/emojis/vander60NSD.png" },
    vander60NSDSHIELD: {
        name: "vander60NSDSHIELD",
        url: "/emojis/vander60NSDSHIELD.png",
    },
    vander60PRAY: { name: "vander60PRAY", url: "/emojis/vander60PRAY.png" },
    vander60RAGE: { name: "vander60RAGE", url: "/emojis/vander60RAGE.png" },
    vander60RAGEFIRE: {
        name: "vander60RAGEFIRE",
        url: "/emojis/vander60RAGEFIRE.png",
    },
    vander60RAIDBOAT: {
        name: "vander60RAIDBOAT",
        url: "/emojis/vander60RAIDBOAT.png",
    },
    vander60RIGGED: {
        name: "vander60RIGGED",
        url: "/emojis/vander60RIGGED.png",
    },
    vander60ROW: { name: "vander60ROW", url: "/emojis/vander60ROW.png" },
    vander60SHOCK: { name: "vander60SHOCK", url: "/emojis/vander60SHOCK.png" },
    vander60SPIN: { name: "vander60SPIN", url: "/emojis/vander60SPIN.png" },
    vander60TING: { name: "vander60TING", url: "/emojis/vander60TING.png" },
    vander60VHV: { name: "vander60VHV", url: "/emojis/vander60VHV.png" },
    vander60VHVSHIELD: {
        name: "vander60VHVSHIELD",
        url: "/emojis/vander60VHVSHIELD.png",
    },
    vander60VP: { name: "vander60VP", url: "/emojis/vander60VP.png" },
    vander60WOHTOKEN: {
        name: "vander60WOHTOKEN",
        url: "/emojis/vander60WOHTOKEN.png",
    },
    vander60WOODBOW: {
        name: "vander60WOODBOW",
        url: "/emojis/vander60WOODBOW.png",
    },
    vander60WOODSHIELD: {
        name: "vander60WOODSHIELD",
        url: "/emojis/vander60WOODSHIELD.png",
    },
    vander60WOODSWORD: {
        name: "vander60WOODSWORD",
        url: "/emojis/vander60WOODSWORD.png",
    },
    vander60WTF: { name: "vander60WTF", url: "/emojis/vander60WTF.png" },
};

/**
 * Build a lookup map for all emoji patterns that should be matched without colons.
 * This is used for Twitch-style emotes like vander60PRAY.
 */
export function getNoColonEmojiMap(): Map<string, CustomEmoji> {
    const map = new Map<string, CustomEmoji>();
    for (const [key, emoji] of Object.entries(CUSTOM_EMOJIS)) {
        // Default to true for matchWithoutColons unless explicitly set to false
        if (emoji.matchWithoutColons !== false) {
            map.set(key, emoji);
        }
    }
    return map;
}

/**
 * Build a lookup map for all emoji patterns that should be matched with colons.
 * Includes primary names and aliases. Only contains custom emojis since
 * standard emojis are handled by the remark-emoji library.
 */
export function getColonEmojiMap(): Map<string, CustomEmoji> {
    const map = new Map<string, CustomEmoji>();

    // Add custom emojis
    for (const [key, emoji] of Object.entries(CUSTOM_EMOJIS)) {
        map.set(key.toLowerCase(), emoji);
        // Add aliases
        if (emoji.aliases) {
            for (const alias of emoji.aliases) {
                map.set(alias.toLowerCase(), emoji);
            }
        }
    }

    return map;
}
